import React, { ReactElement } from 'react';
import { ellipsis, emptyObject } from '@fabric-ui/utils';
import { Box, SxProps, Theme, useTheme } from '@mui/material';

interface IAvTagProps {
  text: ReactElement | string | number;
  isActive?: boolean;
  bg?: string;
  color?: string;
  oneLine?: boolean;
  sx?: SxProps<Theme>;
  isArrowRight?: boolean;
  isArrowLeft?: boolean;
}

const getBackground = (theme: Theme, isActive?: boolean, bg?: string) =>
  bg || (isActive ? theme.palette.colors.primary[150] : theme.palette.colors.neutrals[350]);

const getColor = (theme: Theme, isActive?: boolean, color?: string) =>
  color || (isActive ? theme.palette.colors.primary[400] : theme.palette.colors.neutrals[600]);

function arrowStyle(dir: 'after' | 'before', bgColor: string): SxProps<Theme> {
  const isRight = dir === 'after';
  return {
    position: 'relative',
    [`&::${dir}`]: {
      content: "''",
      display: 'inline-block',
      position: 'absolute',
      top: '50%',
      transform: 'translateY(-50%)',
      ...(isRight
        ? {
            right: -8,
            borderTop: '12px solid transparent',
            borderBottom: '12px solid transparent',
            borderLeft: `8px solid ${bgColor}`,
          }
        : {
            left: -8,
            borderTop: '12px solid transparent',
            borderBottom: '12px solid transparent',
            borderRight: `8px solid ${bgColor}`,
          }),
    },
  };
}

const AvTag: React.FC<IAvTagProps> = ({
  text,
  isActive = false,
  bg,
  color,
  oneLine = false,
  sx = emptyObject,
  isArrowRight = false,
  isArrowLeft = false,
}) => {
  const theme = useTheme();
  const backgroundColor = getBackground(theme, isActive, bg);
  const labelColor = getColor(theme, isActive, color);

  const getArrowStyle = () => {
    if (isArrowLeft) {
      return arrowStyle('before', backgroundColor);
    }
    if (isArrowRight) {
      return arrowStyle('after', backgroundColor);
    }
    return undefined;
  };

  const baseStyles: SxProps<Theme> = {
    display: 'inline-block',
    whiteSpace: 'pre-wrap',
    ...(oneLine ? ellipsis : {}),
    padding: '2px 6px',
    borderRadius: '8px',
    lineHeight: '20px',
    fontWeight: 500,
    fontSize: 13,
    color: labelColor,
    backgroundColor,
    ...(isArrowRight && {
      borderBottomRightRadius: 0,
      borderTopRightRadius: 0,
      paddingLeft: '10px',
      marginRight: '10px', // For arrow space
    }),
    ...(isArrowLeft && {
      borderBottomLeftRadius: 0,
      borderTopLeftRadius: 0,
      paddingRight: '10px',
      marginLeft: '10px', // For arrow space
    }),
    ...getArrowStyle(),
    ...sx,
  };

  if (!text) {
    return null;
  }

  return (
    <Box component="span" sx={baseStyles}>
      {text}
    </Box>
  );
};

export { AvTag };
